<script setup>
  import { ref, onMounted, onUnmounted } from "vue"
  import FlashMessageService from '@services/FlashMessages'
  import GlobalEventBus from '@services/GlobalEventBus'
  import i18n from '@utils/i18n'
  import ModalService from '@services/ModalService'

  const { t } = i18n.global

  const props = defineProps({
    targetAreaId: {
      type: String,
      required: true
    },
    countryCode: {
      type: String,
      required: true
    },
    countryId: {
      type: Number,
      required: true
    },
    preselectedVillages: {
      type: Array,
      default: () => []
    },
    batchData: {
      type: Object,
      required: true
    },
  })

  const mapContainer = ref(undefined)

  // type WalpVillage = {name: string, walp: Kestrel.WrappedAdminLayerPolygon}
  // const walpVillages = ref<Array<WalpVillage>>([])
  const walpVillages = ref([])

  // Add reactive manual survey data for status colors
  const manualSurveyData = ref([])

  // Store hover timeouts for cleanup
  const hoverTimeouts = new Map()

  // Manual survey storage keys
  const getManualSurveyKey = () => {
    const currentUserId = window.gon?.current_user?.id || 'unknown'
    return `manual_surveys_${currentUserId}_${props.targetAreaId}`
  }

  let mapSession = undefined
  let surveyBatchController = undefined

  let initResult = ref(undefined)

  onMounted(async () => {
    try {
      // Clean up expired manual survey data for all users
      cleanupExpiredManualSurveyData()

      mapSession = window.Kestrel.createMapSession(mapContainer.value, "map-container")
      initResult = await mapSession.initialize({
        loadCountryOptions: false,
        countryIso3Code: props.countryCode,
        simplificationStack: { 4: 0 },
      })
      initResult.addZoomControl()

      // Initialize survey batch controller
      initializeSurveyBatchController(props.preselectedVillages)
      surveyBatchController.registerBatchLoadedInformer(handleBatchLoad)

      // Load existing manual survey data
      manualSurveyData.value = loadManualSurveys().surveys

      // Subscribe to events
      GlobalEventBus.subscribe('village-survey-upserted', handleVillageSurveyUpserted)
      GlobalEventBus.subscribe('submit-manual-surveys', submitManualSurveys)
      GlobalEventBus.subscribe('destroy-survey-map-session', () => {
        window.Kestrel.destroyMapSession(mapSession.id)
      })
    } catch (error) {
      console.error('Error during map initialization:', error)
      FlashMessageService.error('Failed to initialize map: ' + error.message)
    }
  })

  // Clean up on component unmount
  onUnmounted(() => {
    // Clear all hover timeouts
    hoverTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId)
    })
    hoverTimeouts.clear()
    
    // Destroy map session if it exists
    if (mapSession && mapSession.id) {
      window.Kestrel.destroyMapSession(mapSession.id)
    }
  })


  function saveManualSurvey(village, surveyData) {
    const key = getManualSurveyKey()
    let manualSurveys = loadManualSurveys()
    const admin4Code = village.walp?.admin4Code
    
    // Find existing survey for this village
    const existingIndex = manualSurveys.surveys.findIndex(s => s.village?.admin_4_code === admin4Code)
    
    const surveyEntry = {
      ...normalizeSurveyData(surveyData),
      village: { admin_4_code: admin4Code }
    }
    
    if (existingIndex !== -1) {
      manualSurveys.surveys[existingIndex] = surveyEntry
    } else {
      manualSurveys.surveys.push(surveyEntry)
    }
    
    localStorage.setItem(key, JSON.stringify(manualSurveys))
    console.log('Manual survey saved to localStorage with key:', key)
    console.log('Final manual surveys data:', manualSurveys)
  }

  function loadManualSurveys() {
    const key = getManualSurveyKey()
    try {
      const data = localStorage.getItem(key)
      if (data) {
        const parsed = JSON.parse(data)
        // Check if data is for current target area and user
        const currentUserId = window.gon?.current_user?.id || 'unknown'
        if (parsed.targetAreaId === props.targetAreaId && parsed.userId === currentUserId) {
          return parsed
        } else {
          // Clear data that doesn't match current target area or user
          localStorage.removeItem(key)
        }
      }
    } catch (error) {
      console.error('Error loading manual surveys:', error)
      localStorage.removeItem(key)
    }
    
    // Return default structure if no data found
    return {
      targetAreaId: props.targetAreaId,
      userId: window.gon?.current_user?.id || 'unknown',
      timestamp: Date.now(),
      surveys: []
    }
  }

  function clearManualSurveys() {
    const key = getManualSurveyKey()
    localStorage.removeItem(key)
    manualSurveyData.value = []
    console.log('Manual surveys cleared from localStorage')
  }

  function cleanupExpiredManualSurveyData() {
    const now = Date.now()
    const maxAge = 1 * 60 * 60 * 1000 // 1 hour
    const currentUserId = window.gon?.current_user?.id || 'unknown'
    
    // Get all localStorage keys
    const keys = Object.keys(localStorage)
    
    // Find all manual survey keys (including current user's for expiration check)
    const manualSurveyKeys = keys.filter(key => 
      key.startsWith('manual_surveys_')
    )
    
    manualSurveyKeys.forEach(key => {
      try {
        const surveyData = localStorage.getItem(key)
        if (surveyData) {
          const data = JSON.parse(surveyData)
          // Check if survey data has expired (too old) OR if it's from a different target area
          if (data.timestamp && (now - data.timestamp) > maxAge) {
            // This survey data has expired, clear it
            localStorage.removeItem(key)
            console.log('Cleaned up expired manual survey data:', key)
          } else if (data.targetAreaId && data.targetAreaId !== props.targetAreaId) {
            // This survey data is from a different target area, clear it
            localStorage.removeItem(key)
            console.log('Cleaned up manual survey data from different target area:', key, 'targetAreaId:', data.targetAreaId)
          } else if (data.userId && data.userId !== currentUserId) {
            // This survey data belongs to a different user, clear it
            localStorage.removeItem(key)
            console.log('Cleaned up manual survey data from different user:', key, 'userId:', data.userId)
          }
        }
      } catch (error) {
        console.error('Error cleaning up manual survey data:', error)
        // If we can't parse the data, it's probably bad data, so remove it
        localStorage.removeItem(key)
      }
    })
  }

  function getExistingVillageSurvey(village) {
    const admin4CodeToFind = village.walp?.admin4Code
    
    // Check manual survey data for existing surveys
    return manualSurveyData.value.find(survey => 
      survey.village?.admin_4_code === admin4CodeToFind
    )
  }

  // normalize survey data to a consistent flat structure
  function normalizeSurveyData(survey) {
    return {
      record_uuid: survey.record_uuid,
      admin_code: survey.admin_code,
      // Flatten attributes
      are_christians: survey.are_christians !== undefined 
        ? survey.are_christians 
        : survey.attributes?.are_christians,
      num_christians: survey.num_christians !== undefined
        ? survey.num_christians
        : survey.attributes?.num_christians,
      // Flatten churches
      churches: (survey.churches || survey.relations?.churches || []).map(church => {
        const churchData = church.attributes || church
        return {
          record_uuid: church.record_uuid || churchData.record_uuid,
          church_type: churchData.church_type || 'church',
          name: churchData.name || '',
          year_started: churchData.year_started || null,
          num_believers: churchData.num_believers || 0,
          num_churches_in_network: churchData.num_churches_in_network || 0,
          lat: churchData.lat || null,
          lng: churchData.lng || null,
          month_started: churchData.month_started || null
        }
      })
    }
  }

  function getSurveyStatusColor(village) {
    const survey = getExistingVillageSurvey(village)
    if (!survey) return null

    const hasChristians = survey.are_christians
    const hasChurches = survey.churches && survey.churches.length > 0

    if (hasChurches) return 'bg-green-500' // Green: has churches (regardless of Christians)
    if (hasChristians && !hasChurches) return 'bg-yellow-500' // Yellow: has Christians, no churches
    if (!hasChristians && !hasChurches) return 'bg-red-500' // Red: no Christians, no churches

    return null
  }

  function initializeSurveyBatchController(preselectedVillages) {
    surveyBatchController = new Kestrel.SurveyBatchSurveyingController(initResult, {
      orgCode: 'TTI',
      batchesBaseUrl: "/api/v1",
      adhocAdminLayerPolygonIds: preselectedVillages,
    })
  }

  async function handleBatchLoad() {
    // we can pull villages off the controller and use this guy for village list on left side of map
    walpVillages.value = surveyBatchController.villages.map(walp => {
      return {
        walp: walp,
        name: walp.name,
      }
    })

    console.log("walpVillages", walpVillages.value)

    // Rebuild adhoc surveys from localStorage if they exist
    if (manualSurveyData.value.length > 0) {
      await rebuildAdhocSurveysFromStorage(manualSurveyData.value)
    }
  }

  async function submitManualSurveys() {
    try {
      // Submit the adhoc batch (this handles all the surveys that were built with buildAdhocVillageSurvey)
      await surveyBatchController.submitAdhocBatch()
      
      // Clear manual surveys from localStorage
      clearManualSurveys()
      
      FlashMessageService.success('Manual surveys submitted successfully!')
      
      // Exit manual survey mode by dispatching event to the target area component
      GlobalEventBus.dispatch('exit-manual-survey-mode')
      
    } catch (error) {
      console.error('Error submitting manual surveys:', error)
      FlashMessageService.error('Failed to submit surveys: ' + error.message)
    }
  }

  function handleVillageSurveyUpserted({ survey, village }) {
    console.log('handleVillageSurveyUpserted called with:', { survey, village })
    const normalizedSurvey = normalizeSurveyData(survey)
    console.log('normalizedSurvey:', normalizedSurvey)
    
    const admin4Code = village.walp?.admin4Code
    console.log('admin4Code:', admin4Code)
    
    // Save to localStorage and update reactive data
    saveManualSurvey(village, survey)
    
    // Update reactive manual survey data for immediate UI updates
    const surveyEntry = { ...normalizedSurvey, village: { admin_4_code: admin4Code } }
    const existingIndex = manualSurveyData.value.findIndex(s => s.village?.admin_4_code === admin4Code)
    
    if (existingIndex !== -1) {
      manualSurveyData.value[existingIndex] = surveyEntry
    } else {
      manualSurveyData.value.push(surveyEntry)
    }
    
    console.log('Manual survey upserted and saved to localStorage:', surveyEntry)
    console.log('Updated manualSurveyData:', manualSurveyData.value)
  }
  
  function handleVillageHoverIn(village) {
    // Clear any existing timeout for this village
    if (hoverTimeouts.has(village.walp.id)) {
      clearTimeout(hoverTimeouts.get(village.walp.id))
      hoverTimeouts.delete(village.walp.id)
    }

    // Start flashing effect while hovering
    let flashCount = 0
    const maxFlashes = 4
    
    const flashVillage = () => {
      if (flashCount < maxFlashes) {
        // Turn off hover
        village.walp.manuallyTriggerHoverOut()
        
        // Turn back on after a short delay
        const timeoutId = setTimeout(() => {
          village.walp.manuallyTriggerHoverIn()
          flashCount++
          
          // Schedule next flash
          if (flashCount < maxFlashes) {
            const nextTimeoutId = setTimeout(flashVillage, 150) // 150ms between flashes
            hoverTimeouts.set(village.walp.id, nextTimeoutId)
          }
        }, 100)
        
        hoverTimeouts.set(village.walp.id, timeoutId)
      }
    }
    
    // Start the flashing
    flashVillage()
  }

  function handleVillageHoverOut(village) {
    // Clear any existing timeout for this village
    if (hoverTimeouts.has(village.walp.id)) {
      clearTimeout(hoverTimeouts.get(village.walp.id))
      hoverTimeouts.delete(village.walp.id)
    }

    // Stop flashing and show the village name normally (no hover effect)
    village.walp.manuallyTriggerHoverOut()
  }

  async function rebuildAdhocSurveysFromStorage(surveys) {
    try {
      console.log('Rebuilding adhoc surveys from localStorage:', surveys)

      // Clear existing adhoc surveys before rebuilding to prevent duplicates
      surveyBatchController.adhocVillageSurveys = []

      for (const survey of surveys) {
        // Convert to the format expected by buildAdhocVillageSurvey
        const villageSurveyAttributes = {
          are_christians: survey.are_christians,
          num_christians: survey.num_christians,
        }
        
        const churches = survey.churches.map(church => ({
          church_type: church.church_type,
          lat: church.lat,
          lng: church.lng,
          num_churches_in_network: church.num_churches_in_network,
          name: church.name,
          year_started: church.year_started,
          month_started: church.month_started,
          num_believers: church.num_believers,
        }))

        // Find the corresponding village in walpVillages to get the walp.id
        const village = walpVillages.value.find(v => v.walp.admin4Code === survey.village.admin_4_code)
        if (village) {
          surveyBatchController.buildAdhocVillageSurvey(
            village.walp.id,
            villageSurveyAttributes,
            churches
          )
        } else {
          console.warn('Could not find village for survey:', survey.village.admin_4_code)
        }
      }
      
      console.log('Successfully rebuilt all adhoc surveys from localStorage')
    } catch (error) {
      console.error('Error rebuilding adhoc surveys from localStorage:', error)
      FlashMessageService.error('Failed to rebuild surveys: ' + error.message)
    }
  }

  function openVillageSurveyModal(village) {
    const existingSurvey = getExistingVillageSurvey(village)
    
    ModalService.spawn('VillageSurveyModal', {
      village,
      batchData: {
        churches: props.batchData.churches,
        target_area: { id: props.targetAreaId },
        training_center_id: props.batchData.training_center_id,
        country_iso3_code: props.countryCode,
      },
      surveyBatchController,
      survey: existingSurvey, // Pass existing survey data if available
      manualMode: true // Always in manual mode for this component
    })
  }
</script>

<template>
  <div class="flex h-full">
    <!-- Sidebar for Selected Villages -->
    <div class="w-64 flex flex-col border-r border-gray-200 h-full">
      <!-- Scrollable Content Area for Selected Villages -->
      <div class="flex-1 overflow-y-auto p-4 min-h-0">
        <div class="flex justify-between items-center mb-2">
          <h3 class="text-sm font-medium text-gray-700">{{ t('modals.batch_edit.selected_villages') }}</h3>
          <span class="text-xs text-gray-500">{{ walpVillages.length }} selected</span>
        </div>

        <div class="space-y-2">
          <div v-if="walpVillages.length === 0"
               class="text-sm text-gray-500 italic">
            {{ t('modals.batch_edit.no_villages_selected') }}
          </div>
          
          <template v-else>
            <div v-for="village in walpVillages"
                 :key="village.walp.id"
                 @mouseenter="handleVillageHoverIn(village)"
                 @mouseleave="handleVillageHoverOut(village)"
                 class="p-2 bg-white/50 backdrop-blur-sm rounded border border-gray-200 hover:bg-white/70 transition-colors duration-200 flex items-center justify-between relative">
              <!-- Survey Status Indicator -->
              <div v-if="getSurveyStatusColor(village)" 
                   :class="getSurveyStatusColor(village)"
                   class="absolute top-0 right-0 w-4 h-4"
                   style="clip-path: polygon(100% 0%, 0% 0%, 100% 100%);">
              </div>
              <div>
                <div class="font-medium text-gray-800 text-sm mb-1">{{ village.name || 'Unknown Village' }}</div>
                <div class="text-xs text-gray-500">
                  {{ t('modals.batch_edit.population') }}: {{ village.walp.population?.toLocaleString() || 'N/A' }}
                </div>
              </div>
              <button
                @click="openVillageSurveyModal(village)"
                class="p-1 transition-colors"
                :class="getExistingVillageSurvey(village) ? 'text-blue-600 hover:text-blue-700' : 'text-gray-400 hover:text-blue-600'"
                :title="getExistingVillageSurvey(village) ? t('modals.batch_show.update_village_survey') : t('modals.batch_show.survey_village')"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                </svg>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 flex flex-col h-full">
      <div class="flex-1 relative">
        <div id="map-container" ref="mapContainer" class="absolute inset-0"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Ensure the map container takes full height */
#map-container {
  height: 100%;
  width: 100%;
}

/* Add styles for the scrollable content */
.overflow-y-auto {
  scrollbar-width: thin;
  scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: rgba(156, 163, 175, 0.5);
  border-radius: 3px;
}
</style> 